import React, { useEffect, useMemo, useRef, useState } from 'react';
import { mergeAll } from './content';
import { EditCtx, PageCtx, sectionProps, pageBg } from './EditableBits';
import { FunnelSections } from './registry';
import { KitMockPay, VipMockForm } from './SharedBlocks';
import { PageWidgets } from './Widgets';
import { useFunnelConfig } from '../ThreeJobs';
import '../../../components/landing/arc/arc.css';

const EDIT_CSS = `
.vx-editable{cursor:pointer;outline:2px dashed transparent;outline-offset:3px;transition:outline-color .12s ease}
.vx-editable:hover{outline-color:rgba(6,182,212,.8)}
.vx-selected{outline:2px solid #06B6D4 !important;outline-offset:3px;cursor:text}
[data-vx-inline]{caret-color:#06B6D4}
.vx-dropzone{height:14px;margin:6px auto;max-width:680px;border-radius:8px;border:2px dashed rgba(6,182,212,.45);background:rgba(6,182,212,.07);transition:height .1s ease,background .1s ease}
.vx-dropzone-over{height:32px;border-color:#06B6D4;background:rgba(6,182,212,.18)}
.vx-ex-wrap{position:relative}
.vx-drag-handle{position:absolute;top:-13px;left:50%;transform:translateX(-50%);z-index:30;cursor:grab;background:#06B6D4;color:#04252b;font:800 10px/1 Manrope,sans-serif;letter-spacing:.08em;border-radius:999px;padding:4px 10px;opacity:0;transition:opacity .12s ease;user-select:none;white-space:nowrap}
.vx-ex-wrap:hover .vx-drag-handle{opacity:1}
.vx-drag-handle:active{cursor:grabbing}
.vx-del-chip{position:absolute;top:-14px;right:-8px;z-index:40;background:#EF4444;color:#fff;font:800 10px/1 Manrope,sans-serif;letter-spacing:.04em;border-radius:999px;padding:4px 9px;cursor:pointer;white-space:nowrap;box-shadow:0 4px 12px rgba(0,0,0,.25)}
.vx-del-chip:hover{background:#DC2626}
`;

const SWATCHES = ['#141414', '#FFFFFF', '#DC2626', '#F59E0B', '#FACC15', '#16A34A', '#2563EB', '#06B6D4', '#7C3AED', '#EC4899'];

/* Floating toolbar for selected words inside an inline-editable element */
const SelToolbar = () => {
  const [pos, setPos] = useState(null);
  const [hex, setHex] = useState('');
  const savedRange = useRef(null);
  const savedHost = useRef(null);

  useEffect(() => {
    const upd = () => {
      if (window.__vxTbLock) return;
      const sel = window.getSelection();
      if (!sel || sel.isCollapsed || !sel.rangeCount) { setPos(null); return; }
      const anchor = sel.anchorNode;
      const host = anchor && (anchor.nodeType === 1 ? anchor : anchor.parentElement);
      const editable = host && host.closest('[data-vx-inline]');
      if (!editable || !editable.isContentEditable) { setPos(null); return; }
      const r = sel.getRangeAt(0).getBoundingClientRect();
      if (!r || (r.width === 0 && r.height === 0)) { setPos(null); return; }
      savedRange.current = sel.getRangeAt(0).cloneRange();
      savedHost.current = editable;
      setPos({ top: r.top, left: r.left + r.width / 2 });
    };
    document.addEventListener('selectionchange', upd);
    return () => document.removeEventListener('selectionchange', upd);
  }, []);

  if (!pos) return null;

  const notify = () => {
    if (savedHost.current) savedHost.current.dispatchEvent(new Event('input', { bubbles: true }));
  };
  const restore = () => {
    if (!savedRange.current) return;
    const sel = window.getSelection();
    sel.removeAllRanges();
    sel.addRange(savedRange.current);
  };
  const apply = (cmd, val) => {
    if (savedHost.current) savedHost.current.focus();
    restore();
    document.execCommand(cmd, false, val);
    notify();
    const sel = window.getSelection();
    if (sel && sel.rangeCount) savedRange.current = sel.getRangeAt(0).cloneRange();
  };
  const run = (cmd, val) => (e) => { e.preventDefault(); e.stopPropagation(); apply(cmd, val); };
  const applyHex = () => {
    const m = hex.trim().match(/^#?([0-9a-fA-F]{6}|[0-9a-fA-F]{3})$/);
    if (!m) return;
    window.__vxTbLock = false;
    apply('foreColor', `#${m[1]}`);
    setHex('');
  };
  const btn = 'w-9 h-9 rounded-lg text-white/90 hover:bg-white/15 flex items-center justify-center transition-colors';
  const left = Math.min(window.innerWidth - 160, Math.max(160, pos.left));

  return (
    <div
      style={{ position: 'fixed', top: Math.max(8, pos.top - 100), left, transform: 'translateX(-50%)', zIndex: 99999 }}
      className="rounded-2xl bg-[#141414] border border-white/10 shadow-2xl px-2.5 py-2 space-y-2"
      data-testid="inline-toolbar"
      onMouseDown={(e) => { if (e.target.tagName !== 'INPUT') e.preventDefault(); }}
    >
      <div className="flex items-center gap-1">
        <button onMouseDown={run('bold')} className={`${btn} text-sm font-black`} title="Bold selection (click again to remove)" data-testid="inline-bold">B</button>
        <button onMouseDown={run('underline')} className={`${btn} text-sm font-bold underline underline-offset-2`} title="Underline selection (click again to remove)" data-testid="inline-underline">U</button>
        <button onMouseDown={run('italic')} className={`${btn} arc-serif text-base italic`} title="Serif accent (click again to remove)" data-testid="inline-accent">A</button>
        <span className="w-px h-5 bg-white/15 mx-1" />
        <button onMouseDown={run('removeFormat')} className="px-3 h-9 rounded-lg text-white/75 hover:bg-white/15 text-[11px] font-bold transition-colors" title="Remove all formatting — back to plain text" data-testid="inline-clear">Plain</button>
      </div>
      <div className="flex items-center gap-1.5">
        {SWATCHES.map((c) => (
          <button
            key={c}
            onMouseDown={run('foreColor', c)}
            className="w-5 h-5 rounded-full border border-white/25 hover:scale-125 transition-transform shrink-0"
            style={{ background: c }}
            title={`Color words ${c}`}
            data-testid={`inline-swatch-${c.slice(1)}`}
          />
        ))}
        <label
          className="relative w-6 h-6 rounded-full border border-white/25 overflow-hidden cursor-pointer shrink-0 hover:scale-110 transition-transform"
          title="Pick any color"
          onMouseDown={() => { window.__vxTbLock = true; }}
        >
          <span className="absolute inset-0" style={{ background: 'conic-gradient(#DC2626,#F59E0B,#FACC15,#16A34A,#2563EB,#7C3AED,#EC4899,#DC2626)' }} />
          <input
            type="color"
            onFocus={() => { window.__vxTbLock = true; }}
            onInput={(e) => apply('foreColor', e.target.value)}
            onBlur={() => setTimeout(() => { window.__vxTbLock = false; }, 200)}
            className="absolute inset-0 opacity-0 cursor-pointer"
            data-testid="inline-colorpicker"
          />
        </label>
        <input
          value={hex}
          onChange={(e) => setHex(e.target.value)}
          onFocus={() => { window.__vxTbLock = true; }}
          onBlur={() => setTimeout(() => { window.__vxTbLock = false; }, 150)}
          onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); applyHex(); } }}
          placeholder="#hex ⏎"
          className="w-[70px] h-7 px-1.5 rounded-md bg-white/10 border border-white/15 text-[11px] text-white placeholder-white/30 focus:outline-none focus:border-cyan-400"
          data-testid="inline-hex"
        />
      </div>
    </div>
  );
};

export default function FunnelEditorPreviewPage() {
  const page = useMemo(() => new URLSearchParams(window.location.search).get('page') || 'lander', []);
  const config = useFunnelConfig();
  const [sections, setSections] = useState(() => mergeAll({})[page] || []);
  const [jobs, setJobs] = useState(() => sectionProps(mergeAll({}).thanks, 'three_jobs'));
  const [selected, setSelected] = useState(null);
  const [drag, setDrag] = useState(null);

  const post = (msg) => { try { window.parent.postMessage({ vx: 'funnel-editor-preview', ...msg }, '*'); } catch { /* noop */ } };

  useEffect(() => {
    const onMsg = (e) => {
      const d = e.data || {};
      if (d.vx !== 'funnel-editor') return;
      if (d.type === 'content') {
        if (Array.isArray(d.sections)) setSections(d.sections);
        if (d.jobs) setJobs(d.jobs);
      }
      if (d.type === 'select') setSelected(d.id || null);
      if (d.type === 'scroll' && d.id) {
        const el = document.querySelector(`[data-vx-sec="${d.id}"]`);
        if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }
    };
    window.addEventListener('message', onMsg);
    post({ type: 'ready' });
    return () => window.removeEventListener('message', onMsg);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const edit = {
    selected,
    drag,
    onSelect: (sec, k) => {
      setSelected(`${sec}:${k ?? ''}`);
      post({ type: 'select', sec, k: k ?? null });
    },
    onInline: (sec, path, t) => post({ type: 'inline', sec, path, t }),
    onHide: (sec, path) => {
      setSelected(null);
      post({ type: 'hide', sec, path });
    },
    onDragStart: (sec, id) => setDrag({ sec, id }),
    onDragEnd: () => setDrag(null),
    onDropExtra: (toSec, at, beforeId) => {
      setDrag((cur) => {
        if (cur) post({ type: 'extraDrop', fromSec: cur.sec, fromId: cur.id, toSec, at, beforeId: beforeId || null });
        return null;
      });
    },
  };

  const vipMainSec = sections.find((s) => s.type === 'vip_main');
  const handlers = {
    openQuiz: () => {},
    bump: false,
    setBump: () => {},
    decline: () => {},
    declining: false,
    checkoutSlot: <KitMockPay />,
    vipSlot: vipMainSec ? <VipMockForm sec={vipMainSec.id} p={vipMainSec.props || {}} /> : null,
  };

  return (
    <EditCtx.Provider value={edit}>
      <PageCtx.Provider value={{ config, handlers, jobs }}>
        <style>{EDIT_CSS}</style>
        <SelToolbar />
        <div className="min-h-screen arc-bone pb-6" style={{ background: pageBg(sections) || undefined }} data-testid={`funnel-preview-${page}`}>
          <FunnelSections sections={sections} />
          {(page === 'lander' || page === 'thanks') && (
            <footer className="border-t border-black/[0.06] py-8 text-center">
              <p className="arc-body text-xs text-[#141414]/40">© {new Date().getFullYear()} Voxinity AI. All rights reserved.</p>
            </footer>
          )}
          <PageWidgets sections={sections} />
        </div>
      </PageCtx.Provider>
    </EditCtx.Provider>
  );
}
